import React, { useEffect } from 'react';
import { useImageGallery } from '../hooks/useImageGallery';

interface ImageGalleryProps {
  images: string[];
  className?: string;
  showThumbnails?: boolean;
  thumbnailSize?: 'sm' | 'md' | 'lg';
  lightbox?: boolean;
  onImageClick?: (imageUrl: string, index: number) => void;
}

export default function ImageGallery({
  images,
  className = '',
  showThumbnails = true,
  thumbnailSize = 'md',
  lightbox = true,
  onImageClick
}: ImageGalleryProps) {
  const {
    images: galleryImages,
    selectedImage,
    currentIndex,
    isLightboxOpen,
    openLightbox,
    closeLightbox,
    nextImage,
    prevImage,
    setImages
  } = useImageGallery(images);

  useEffect(() => {
    setImages(images);
  }, [images, setImages]);

  // Manejo de teclas para el lightbox
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isLightboxOpen) return;

      switch (event.key) {
        case 'Escape':
          closeLightbox();
          break;
        case 'ArrowLeft':
          prevImage();
          break;
        case 'ArrowRight':
          nextImage();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isLightboxOpen, closeLightbox, nextImage, prevImage]);

  if (!galleryImages.length) {
    return (
      <div className={`no-images text-center text-muted ${className}`}>
        <i className="fa fa-image fa-3x mb-3"></i>
        <p>No hay imágenes disponibles</p>
      </div>
    );
  }

  const getThumbnailClasses = () => {
    const baseClasses = 'img-fluid rounded shadow-sm';
    switch (thumbnailSize) {
      case 'sm': return `${baseClasses} gallery-thumb-sm`;
      case 'lg': return `${baseClasses} gallery-thumb-lg`;
      default: return `${baseClasses} gallery-thumb-md`;
    }
  };

  const handleImageClick = (imageUrl: string, index: number) => {
    if (onImageClick) {
      onImageClick(imageUrl, index);
    } else if (lightbox) {
      openLightbox(imageUrl);
    }
  };

  return (
    <>
      <div className={`image-gallery ${className}`}>
        {/* Imagen principal */}
        {galleryImages.length > 0 && (
          <div className="main-image mb-3">
            <img
              src={galleryImages[0]}
              alt="Imagen principal"
              className="img-fluid rounded shadow-sm w-100"
              style={{ 
                aspectRatio: '16/9', 
                objectFit: 'cover',
                cursor: lightbox ? 'zoom-in' : 'default',
                maxHeight: '400px'
              }}
              onClick={() => handleImageClick(galleryImages[0], 0)}
            />
            {galleryImages.length > 1 && (
              <div className="image-counter position-absolute bottom-0 end-0 bg-dark bg-opacity-75 text-white px-2 py-1 rounded-top-start">
                <small>
                  <i className="fa fa-images me-1"></i>
                  {galleryImages.length} fotos
                </small>
              </div>
            )}
          </div>
        )}

        {/* Miniaturas */}
        {showThumbnails && galleryImages.length > 1 && (
          <div className="thumbnail-grid">
            <div className="row g-2">
              {galleryImages.slice(1, 7).map((imageUrl, index) => (
                <div key={index} className="col-2">
                  <div className="position-relative">
                    <img
                      src={imageUrl}
                      alt={`Miniatura ${index + 2}`}
                      className={getThumbnailClasses()}
                      style={{ 
                        aspectRatio: '1',
                        objectFit: 'cover',
                        cursor: lightbox ? 'zoom-in' : 'default',
                        width: '100%'
                      }}
                      onClick={() => handleImageClick(imageUrl, index + 1)}
                    />
                    {/* Overlay para mostrar más imágenes */}
                    {index === 5 && galleryImages.length > 7 && (
                      <div
                        className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-75 text-white rounded cursor-pointer"
                        onClick={() => handleImageClick(imageUrl, index + 1)}
                      >
                        <span className="fw-bold">
                          +{galleryImages.length - 7}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightbox && isLightboxOpen && selectedImage && (
        <div className="lightbox-overlay position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-90" style={{ zIndex: 9999 }}>
          <div className="lightbox-container position-relative">
            {/* Cerrar */}
            <button
              className="btn btn-outline-light position-absolute top-0 end-0 m-3"
              style={{ zIndex: 10000 }}
              onClick={closeLightbox}
            >
              <i className="fa fa-times"></i>
            </button>

            {/* Navegación anterior */}
            {galleryImages.length > 1 && (
              <button
                className="btn btn-outline-light position-absolute top-50 start-0 translate-middle-y ms-3"
                style={{ zIndex: 10000 }}
                onClick={prevImage}
              >
                <i className="fa fa-chevron-left"></i>
              </button>
            )}

            {/* Imagen */}
            <img
              src={selectedImage}
              alt={`Imagen ${currentIndex + 1}`}
              className="lightbox-image img-fluid rounded"
              style={{ 
                maxWidth: '90vw',
                maxHeight: '90vh',
                objectFit: 'contain'
              }}
            />

            {/* Navegación siguiente */}
            {galleryImages.length > 1 && (
              <button
                className="btn btn-outline-light position-absolute top-50 end-0 translate-middle-y me-3"
                style={{ zIndex: 10000 }}
                onClick={nextImage}
              >
                <i className="fa fa-chevron-right"></i>
              </button>
            )}

            {/* Contador */}
            <div className="position-absolute bottom-0 start-50 translate-middle-x mb-3 bg-dark bg-opacity-75 text-white px-3 py-2 rounded">
              <small>{currentIndex + 1} de {galleryImages.length}</small>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .gallery-thumb-sm {
          height: 60px !important;
        }
        .gallery-thumb-md {
          height: 80px !important;
        }
        .gallery-thumb-lg {
          height: 100px !important;
        }
        .main-image {
          position: relative;
        }
        .lightbox-overlay {
          backdrop-filter: blur(5px);
        }
        .cursor-pointer {
          cursor: pointer;
        }
        .image-gallery .main-image:hover img {
          transform: scale(1.02);
          transition: transform 0.2s ease-in-out;
        }
        .thumbnail-grid img:hover {
          transform: scale(1.05);
          transition: transform 0.2s ease-in-out;
        }
      `}</style>
    </>
  );
}